import { translate, useLanguage } from '@/lib/i18n';
import { PageLoader } from '@/components/ui/loader';
import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { EmptyState } from '@/components/shared/page';
import { Button } from '@/components/ui/button';
import { useSession } from '@/features/auth/use-session';
import { usePermission } from '@/hooks/use-permission';
import { LoginPage } from '@/features/auth/login-page';
import { SignupPage } from '@/features/auth/signup-page';
import {
  AcceptInvitePage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '@/features/auth/password-pages';
import { AccountPage } from '@/features/auth/account-page';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { UsersPage } from '@/features/admin/users-page';
import { RolesPage } from '@/features/admin/roles-page';
import { TeamsPage } from '@/features/admin/teams-page';
import { OrganizationPage } from '@/features/admin/organization-page';
import { AuditPage } from '@/features/admin/audit-page';
import { PlatformPage } from '@/features/platform/platform-page';
import { PrivacyPage, TermsPage } from '@/features/marketing/legal-pages';
import { LeadsPage } from '@/features/crm/leads-page';
import { CompaniesPage } from '@/features/crm/companies-page';
import { ContactsPage } from '@/features/crm/contacts-page';
import { DealsBoardPage } from '@/features/crm/deals-board';
import { DealsListPage } from '@/features/crm/deals-list-page';
import { DealDetailPage } from '@/features/crm/deal-detail-page';
import { QuotesPage } from '@/features/crm/quotes-page';
import { QuoteBuilderPage } from '@/features/crm/quote-builder-page';
import { MyDayPage } from '@/features/crm/my-day-page';
import { PublicQuotePage } from '@/features/crm/public-quote-page';
import { FinanceDashboardPage } from '@/features/finance/finance-dashboard-page';
import { InvoicesPage } from '@/features/finance/invoices-page';
import { InvoiceBuilderPage } from '@/features/finance/invoice-builder-page';
import { PaymentsPage } from '@/features/finance/payments-page';
import { ExpensesPage } from '@/features/finance/expenses-page';
import { SubscriptionsPage } from '@/features/finance/subscriptions-page';
import { FinanceReportsPage } from '@/features/finance/finance-reports-page';
import { FinanceSettingsPage } from '@/features/finance/finance-settings-page';
import { PublicInvoicePage } from '@/features/finance/public-invoice-page';
import { HomePage } from '@/features/marketing/home-page';
import { DocumentsPage } from '@/features/vault/documents-page';
import { DocumentDetailPage } from '@/features/vault/document-detail-page';
import { TemplatesPage } from '@/features/vault/templates-page';
import { PublicSharePage } from '@/features/vault/public-share-page';
import { PublicSignPage } from '@/features/vault/public-sign-page';
import { CampaignsPage } from '@/features/vault/campaigns-page';
import { CampaignBuilderPage } from '@/features/vault/campaign-builder-page';
import { SegmentsPage } from '@/features/vault/segments-page';
import { JourneysPage } from '@/features/vault/journeys-page';
import { WhatsappPage } from '@/features/vault/whatsapp-page';
import { MarketingSettingsPage } from '@/features/vault/marketing-settings-page';
import { ProjectsPage } from '@/features/delivery/projects-page';
import { ProjectDetailPage } from '@/features/delivery/project-detail-page';
import { WorkBoardPage } from '@/features/delivery/work-board-page';
import { TimesheetsPage } from '@/features/delivery/timesheets-page';
import { TicketsPage } from '@/features/delivery/tickets-page';
import { TicketDetailPage } from '@/features/delivery/ticket-detail-page';
import { ContractsPage } from '@/features/delivery/contracts-page';
import { ContractDetailPage } from '@/features/delivery/contract-detail-page';
import { ServiceSettingsPage } from '@/features/delivery/service-settings-page';
import { ChatPage } from '@/features/chat/chat-page';
import { ForecastPage } from '@/features/forecast/forecast-page';
import { BudgetsPage } from '@/features/forecast/budgets-page';
import { InsightsDashboardPage } from '@/features/reports/dashboard-page';
import { ReportsPage } from '@/features/reports/reports-page';
import { WorkflowsPage } from '@/features/automation/workflows-page';
import { PortalUsersPage } from '@/features/portal/portal-users-page';
import { PortalShell } from '@/features/portal/portal-shell';
import {
  PortalAcceptInvitePage,
  PortalForgotPasswordPage,
  PortalLoginPage,
  PortalResetPasswordPage,
} from '@/features/portal/portal-auth-pages';
import {
  PortalAccountPage,
  PortalDashboardPage,
  PortalDocumentsPage,
  PortalInvoicesPage,
  PortalProjectPage,
  PortalProjectsPage,
  PortalQuotesPage,
  PortalServicesPage,
  PortalTicketPage,
  PortalTicketsPage,
} from '@/features/portal/portal-pages';
import { PipelinesPage } from '@/features/crm/pipelines-page';
import { PropertiesPage } from '@/features/crm/properties-page';
import { DealsCalendarPage } from '@/features/crm/deals-calendar';
import { CustomerSuccessPage, PublicSurveyPage, SurveyResultsPage, SurveysPage, TaskQueuesPage } from '@/features/cx/cx-pages';
import { PaymentLinksPage, PublicPaymentLinkPage } from '@/features/commerce/payment-links';
import { IntegrationsPage } from '@/features/admin/integrations-page';
import { AiAgentPage, AnalystPage } from '@/features/ai/ai-pages';
import { SsoCallbackPage } from '@/features/auth/sso';
import { PlaylistDetailPage, PlaylistsPage, SheetSyncPage } from '@/features/sales/extras-pages';
import { SalesAnalyticsPage } from '@/features/reports/sales-analytics-page';
import { LeadScoringPage } from '@/features/crm/lead-scoring-page';
import { BlogEditorPage, BlogPage, EventDetailPage, EventsPage, PublicBlogPage, PublicEventPage } from '@/features/growth/content-pages';
import { DuplicatesPage } from '@/features/crm/duplicates-page';
import { DigitalPipelinePage } from '@/features/crm/digital-pipeline-page';
import { InboxPage } from '@/features/inbox/inbox-page';
import { ChannelsPage } from '@/features/inbox/channels-page';
import { SalesbotsPage } from '@/features/inbox/salesbots-page';
import { CompanyRecordPage, ContactRecordPage, LeadRecordPage } from '@/features/crm/record-page';
import { ImportPage } from '@/features/sales/import-page';
import { SequencesPage } from '@/features/sales/sequences-page';
import { MeetingsPage } from '@/features/sales/meetings-page';
import { EmailsPage } from '@/features/sales/emails-page';
import { CallsPage } from '@/features/sales/calls-page';
import { PlaybooksPage } from '@/features/sales/playbooks-page';
import { ActivityPage } from '@/features/sales/activity-page';
import { RecycleBinPage } from '@/features/sales/recycle-bin-page';
import { MailboxPage } from '@/features/sales/mailbox-page';
import { TelephonyPage } from '@/features/sales/telephony-page';
import { PublicFormPage } from '@/features/growth/public-form';
import { PublicLandingPage } from '@/features/growth/page-blocks';
import { HelpArticlePage, HelpHomePage, HelpListPage } from '@/features/growth/help-center';
import { FormsPage } from '@/features/growth/forms-page';
import { PageEditorPage, PagesPage } from '@/features/growth/pages-page';
import { WebsitePage } from '@/features/growth/website-page';
import { KnowledgeBasePage } from '@/features/growth/kb-page';
import { AdsPage } from '@/features/growth/ads-page';
import { SocialPage } from '@/features/growth/social-page';
import { PaymentReturnPage } from '@/features/commerce/commerce';
import { BillingPage } from '@/features/commerce/billing-page';
import { ObjectRecordsPage, ObjectsPage } from '@/features/objects/objects-page';
import { PublicBookingPage, PublicManageMeetingPage } from '@/features/sales/public-booking-page';
import { LeadsBoardPage } from '@/features/crm/leads-board';
import { useCanSeeDashboard, useCanSeeMyDay, useHomeRoute } from '@/components/layout/nav';
import { ErrorPage } from './error-page';

/** Sends anyone without a session to the login screen, remembering where they were. */
function RequireAuth() {
  const { data: user, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <PageLoader className="h-screen" />
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}

/** Guards a route behind a permission, with an explanation rather than a blank page. */
function RequirePermission({ permission, anyOf }: { permission?: string; anyOf?: string[] }) {
  const { can, canAny } = usePermission();
  const home = useHomeRoute();

  if ((permission && !can(permission)) || (anyOf && !canAny(...anyOf))) {
    return (
      <EmptyState
        title={translate('You do not have access to this page', useLanguage.getState().lang)}
        description={translate('Ask an administrator if you think you should. They can grant it from Roles.', useLanguage.getState().lang)}
        action={
          <Button asChild variant="outline">
            <a href={home}>Back to home</a>
          </Button>
        }
      />
    );
  }

  return <Outlet />;
}

/**
 * The overview screens have no permission of their own; they draw on other
 * modules. Somebody whose role touches none of those — a lead-entry role — is
 * sent to the first screen they can actually use instead of an empty page.
 */
function DashboardGate() {
  const home = useHomeRoute();
  return useCanSeeDashboard() ? <DashboardPage /> : <Navigate to={home} replace />;
}

function MyDayGate() {
  const home = useHomeRoute();
  return useCanSeeMyDay() ? <MyDayPage /> : <Navigate to={home} replace />;
}

export const router = createBrowserRouter([
  // The signed-out front door. Left public even for signed-in people: the header
  // simply offers the dashboard instead of a sign-in link.
  { path: '/', element: <HomePage />, errorElement: <ErrorPage /> },

  { path: '/login', element: <LoginPage />, errorElement: <ErrorPage /> },
  { path: '/signup', element: <SignupPage />, errorElement: <ErrorPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },

  // The client-facing links. No session, no app shell — the token is the credential.
  { path: '/q/:token', element: <PublicQuotePage />, errorElement: <ErrorPage /> },
  { path: '/i/:token', element: <PublicInvoicePage />, errorElement: <ErrorPage /> },
  { path: '/d/:token', element: <PublicSharePage />, errorElement: <ErrorPage /> },
  { path: '/sign/:token', element: <PublicSignPage />, errorElement: <ErrorPage /> },
  { path: '/meet/manage/:token', element: <PublicManageMeetingPage />, errorElement: <ErrorPage /> },
  { path: '/meet/:slug', element: <PublicBookingPage />, errorElement: <ErrorPage /> },
  { path: '/s/:token', element: <PublicSurveyPage />, errorElement: <ErrorPage /> },
  { path: '/e/:slug', element: <PublicEventPage />, errorElement: <ErrorPage /> },
  { path: '/blog/:slug', element: <PublicBlogPage />, errorElement: <ErrorPage /> },
  { path: '/blog/:slug/:post', element: <PublicBlogPage />, errorElement: <ErrorPage /> },
  { path: '/sso/callback', element: <SsoCallbackPage />, errorElement: <ErrorPage /> },
  { path: '/pl/:token', element: <PublicPaymentLinkPage />, errorElement: <ErrorPage /> },
  { path: '/f/:formKey', element: <PublicFormPage />, errorElement: <ErrorPage /> },
  { path: '/pay/return/:attemptId', element: <PaymentReturnPage />, errorElement: <ErrorPage /> },
  { path: '/lp/:slug', element: <PublicLandingPage />, errorElement: <ErrorPage /> },
  { path: '/help/:slug', element: <HelpHomePage />, errorElement: <ErrorPage /> },
  { path: '/help/:slug/search', element: <HelpListPage />, errorElement: <ErrorPage /> },
  { path: '/help/:slug/c/:category', element: <HelpListPage />, errorElement: <ErrorPage /> },
  { path: '/help/:slug/:article', element: <HelpArticlePage />, errorElement: <ErrorPage /> },

  // The client portal. A separate tree with a separate session: PortalShell
  // authenticates against portal_users, so nothing here touches RequireAuth
  // and a staff token opens none of it.
  { path: '/portal/login', element: <PortalLoginPage />, errorElement: <ErrorPage /> },
  { path: '/portal/accept-invite', element: <PortalAcceptInvitePage /> },
  { path: '/portal/forgot-password', element: <PortalForgotPasswordPage /> },
  { path: '/portal/reset-password', element: <PortalResetPasswordPage /> },
  {
    path: '/portal',
    element: <PortalShell />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <PortalDashboardPage /> },
      { path: 'projects', element: <PortalProjectsPage /> },
      { path: 'projects/:id', element: <PortalProjectPage /> },
      { path: 'tickets', element: <PortalTicketsPage /> },
      { path: 'tickets/:id', element: <PortalTicketPage /> },
      { path: 'services', element: <PortalServicesPage /> },
      { path: 'documents', element: <PortalDocumentsPage /> },
      { path: 'invoices', element: <PortalInvoicesPage /> },
      { path: 'quotes', element: <PortalQuotesPage /> },
      { path: 'account', element: <PortalAccountPage /> },
    ],
  },

  {
    element: <RequireAuth />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: 'dashboard', element: <DashboardGate /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'my-day', element: <MyDayGate /> },
          { path: 'reports/sales-analytics', element: <RequirePermission permission="report.report.view" />, children: [{ index: true, element: <SalesAnalyticsPage /> }] },
          { path: 'settings/lead-scoring', element: <RequirePermission permission="crm.lead.view" />, children: [{ index: true, element: <LeadScoringPage /> }] },
          {
            path: 'marketing/blog',
            element: <RequirePermission permission="marketing.blog.view" />,
            children: [
              { index: true, element: <BlogPage /> },
              { path: ':postId', element: <BlogEditorPage /> },
            ],
          },
          {
            path: 'marketing/events',
            element: <RequirePermission permission="marketing.event.view" />,
            children: [
              { index: true, element: <EventsPage /> },
              { path: ':eventId', element: <EventDetailPage /> },
            ],
          },
          { path: 'crm/google-sheets', element: <RequirePermission permission="crm.import.view" />, children: [{ index: true, element: <SheetSyncPage /> }] },
          {
            path: 'sales/coaching',
            element: <RequirePermission permission="crm.call.view" />,
            children: [
              { index: true, element: <PlaylistsPage /> },
              { path: ':playlistId', element: <PlaylistDetailPage /> },
            ],
          },
          { path: 'ai-analyst', element: <RequirePermission permission="report.report.view" />, children: [{ index: true, element: <AnalystPage /> }] },
          { path: 'inbox/ai-agent', element: <RequirePermission permission="inbox.channel.view" />, children: [{ index: true, element: <AiAgentPage /> }] },
          { path: 'task-queues', element: <RequirePermission permission="crm.task.view" />, children: [{ index: true, element: <TaskQueuesPage /> }] },
          { path: 'finance/payment-links', element: <RequirePermission permission="finance.paymentlink.view" />, children: [{ index: true, element: <PaymentLinksPage /> }] },
          { path: 'admin/integrations', element: <RequirePermission permission="admin.integration.view" />, children: [{ index: true, element: <IntegrationsPage /> }] },

          // Every staff role holds the chat permission; it exists so a role
          // can be built without it. Membership decides what each person reads.
          {
            path: 'chat',
            element: <RequirePermission permission="chat.channel.view" />,
            children: [
              { index: true, element: <ChatPage /> },
              { path: ':channelId', element: <ChatPage /> },
            ],
          },

          {
            path: 'inbox',
            children: [
              {
                element: <RequirePermission permission="inbox.conversation.view" />,
                children: [{ index: true, element: <InboxPage /> }],
              },
              {
                path: 'channels',
                element: <RequirePermission permission="inbox.channel.view" />,
                children: [{ index: true, element: <ChannelsPage /> }],
              },
              {
                path: 'salesbots',
                element: <RequirePermission permission="inbox.salesbot.view" />,
                children: [{ index: true, element: <SalesbotsPage /> }],
              },
            ],
          },

          {
            path: 'sales',
            children: [
              { path: 'sequences', element: <RequirePermission permission="crm.sequence.view" />, children: [{ index: true, element: <SequencesPage /> }] },
              { path: 'meetings', element: <RequirePermission permission="crm.meeting.view" />, children: [{ index: true, element: <MeetingsPage /> }] },
              { path: 'emails', element: <RequirePermission permission="crm.email.view" />, children: [{ index: true, element: <EmailsPage /> }] },
              { path: 'calls', element: <RequirePermission permission="crm.call.view" />, children: [{ index: true, element: <CallsPage /> }] },
              { path: 'playbooks', element: <RequirePermission permission="crm.playbook.view" />, children: [{ index: true, element: <PlaybooksPage /> }] },
              { path: 'mailbox', element: <RequirePermission permission="crm.email.send" />, children: [{ index: true, element: <MailboxPage /> }] },
              { path: 'telephony', element: <RequirePermission permission="crm.telephony.view" />, children: [{ index: true, element: <TelephonyPage /> }] },
              { path: 'activity', element: <RequirePermission permission="crm.quota.view" />, children: [{ index: true, element: <ActivityPage /> }] },
              {
                path: 'import',
                element: <RequirePermission anyOf={['crm.import.view', 'crm.lead.export', 'crm.contact.export', 'crm.company.export', 'crm.deal.export']} />,
                children: [{ index: true, element: <ImportPage /> }],
              },
              {
                path: 'recycle-bin',
                element: <RequirePermission anyOf={['crm.lead.delete', 'crm.contact.delete', 'crm.company.delete', 'crm.deal.delete']} />,
                children: [{ index: true, element: <RecycleBinPage /> }],
              },
            ],
          },

          {
            path: 'insights',
            element: <RequirePermission permission="report.dashboard.view" />,
            children: [{ index: true, element: <InsightsDashboardPage /> }],
          },
          {
            path: 'reports',
            element: <RequirePermission permission="report.report.view" />,
            children: [{ index: true, element: <ReportsPage /> }],
          },

          {
            path: 'crm/leads',
            element: <RequirePermission permission="crm.lead.view" />,
            children: [
              { index: true, element: <LeadsPage /> },
              { path: 'board', element: <LeadsBoardPage /> },
              { path: ':leadId', element: <LeadRecordPage /> },
            ],
          },
          {
            path: 'crm/companies',
            element: <RequirePermission permission="crm.company.view" />,
            children: [
              { index: true, element: <CompaniesPage /> },
              { path: ':companyId', element: <CompanyRecordPage /> },
            ],
          },
          {
            path: 'crm/contacts',
            element: <RequirePermission permission="crm.contact.view" />,
            children: [
              { index: true, element: <ContactsPage /> },
              { path: ':contactId', element: <ContactRecordPage /> },
            ],
          },
          {
            path: 'crm/deals',
            element: <RequirePermission permission="crm.deal.view" />,
            children: [
              { index: true, element: <DealsBoardPage /> },
              { path: 'list', element: <DealsListPage /> },
              { path: 'calendar', element: <DealsCalendarPage /> },
              { path: ':dealId', element: <DealDetailPage /> },
            ],
          },
          {
            path: 'crm/quotes',
            element: <RequirePermission permission="crm.quote.view" />,
            children: [
              { index: true, element: <QuotesPage /> },
              { path: 'new', element: <QuoteBuilderPage /> },
              { path: ':quoteId', element: <QuoteBuilderPage /> },
            ],
          },
          {
            path: 'crm/digital-pipeline',
            element: <RequirePermission permission="admin.workflow.view" />,
            children: [
              { index: true, element: <Navigate to="lead" replace /> },
              { path: ':entityType', element: <DigitalPipelinePage /> },
            ],
          },
          {
            path: 'crm/pipelines',
            element: <RequirePermission permission="crm.pipeline.view" />,
            children: [
              { index: true, element: <PipelinesPage /> },
              // Deal boards and lead boards, one editor.
              { path: ':entityType', element: <PipelinesPage /> },
            ],
          },

          {
            path: 'finance',
            children: [
              {
                element: <RequirePermission permission="finance.invoice.view" />,
                children: [
                  { index: true, element: <FinanceDashboardPage /> },
                  { path: 'invoices', element: <InvoicesPage /> },
                  { path: 'invoices/new', element: <InvoiceBuilderPage /> },
                  { path: 'invoices/:invoiceId', element: <InvoiceBuilderPage /> },
                ],
              },
              {
                path: 'payments',
                element: <RequirePermission permission="finance.payment.view" />,
                children: [{ index: true, element: <PaymentsPage /> }],
              },
              {
                path: 'expenses',
                element: <RequirePermission permission="finance.expense.view" />,
                children: [{ index: true, element: <ExpensesPage /> }],
              },
              {
                path: 'subscriptions',
                element: <RequirePermission permission="product.subscription.view" />,
                children: [{ index: true, element: <SubscriptionsPage /> }],
              },
              {
                path: 'reports',
                element: <RequirePermission permission="finance.ledger.view" />,
                children: [{ index: true, element: <FinanceReportsPage /> }],
              },
              {
                path: 'forecast',
                element: <RequirePermission permission="finance.forecast.view" />,
                children: [{ index: true, element: <ForecastPage /> }],
              },
              {
                path: 'budgets',
                element: <RequirePermission permission="finance.budget.view" />,
                children: [{ index: true, element: <BudgetsPage /> }],
              },
              {
                path: 'settings',
                element: <RequirePermission permission="finance.settings.view" />,
                children: [{ index: true, element: <FinanceSettingsPage /> }],
              },
            ],
          },

          {
            path: 'delivery',
            children: [
              {
                path: 'projects',
                element: <RequirePermission permission="project.project.view" />,
                children: [
                  { index: true, element: <ProjectsPage /> },
                  { path: ':projectId', element: <ProjectDetailPage /> },
                ],
              },
              {
                path: 'board',
                element: <RequirePermission permission="project.task.view" />,
                children: [{ index: true, element: <WorkBoardPage /> }],
              },
              {
                path: 'timesheets',
                element: <RequirePermission permission="project.timesheet.view" />,
                children: [{ index: true, element: <TimesheetsPage /> }],
              },
            ],
          },

          {
            path: 'service',
            children: [
              { path: 'knowledge-base', element: <RequirePermission permission="service.kb.view" />, children: [{ index: true, element: <KnowledgeBasePage /> }] },
              {
                path: 'surveys',
                element: <RequirePermission permission="service.survey.view" />,
                children: [
                  { index: true, element: <SurveysPage /> },
                  { path: ':surveyId', element: <SurveyResultsPage /> },
                ],
              },
              { path: 'customer-success', element: <RequirePermission permission="crm.company.view" />, children: [{ index: true, element: <CustomerSuccessPage /> }] },
              {
                path: 'tickets',
                element: <RequirePermission permission="service.ticket.view" />,
                children: [
                  { index: true, element: <TicketsPage /> },
                  { path: ':ticketId', element: <TicketDetailPage /> },
                ],
              },
              {
                path: 'contracts',
                element: <RequirePermission permission="service.contract.view" />,
                children: [
                  { index: true, element: <ContractsPage /> },
                  { path: ':contractId', element: <ContractDetailPage /> },
                ],
              },
              {
                path: 'settings',
                element: <RequirePermission permission="service.sla.view" />,
                children: [{ index: true, element: <ServiceSettingsPage /> }],
              },
            ],
          },

          {
            path: 'documents',
            element: <RequirePermission permission="file.document.view" />,
            children: [
              { index: true, element: <DocumentsPage /> },
              { path: 'templates', element: <TemplatesPage /> },
              { path: ':documentId', element: <DocumentDetailPage /> },
            ],
          },

          {
            path: 'marketing',
            children: [
              { path: 'forms', element: <RequirePermission permission="marketing.form.view" />, children: [{ index: true, element: <FormsPage /> }] },
              {
                path: 'pages',
                element: <RequirePermission permission="marketing.page.view" />,
                children: [
                  { index: true, element: <PagesPage /> },
                  { path: ':pageId', element: <PageEditorPage /> },
                ],
              },
              { path: 'website', element: <RequirePermission permission="marketing.site.view" />, children: [{ index: true, element: <WebsitePage /> }] },
              { path: 'ads', element: <RequirePermission permission="marketing.ads.view" />, children: [{ index: true, element: <AdsPage /> }] },
              { path: 'social', element: <RequirePermission permission="marketing.social.view" />, children: [{ index: true, element: <SocialPage /> }] },
              {
                path: 'campaigns',
                element: <RequirePermission permission="marketing.campaign.view" />,
                children: [
                  { index: true, element: <CampaignsPage /> },
                  { path: ':campaignId', element: <CampaignBuilderPage /> },
                ],
              },
              {
                path: 'segments',
                element: <RequirePermission permission="marketing.segment.view" />,
                children: [{ index: true, element: <SegmentsPage /> }],
              },
              {
                path: 'journeys',
                element: <RequirePermission permission="marketing.campaign.view" />,
                children: [{ index: true, element: <JourneysPage /> }],
              },
              {
                path: 'whatsapp',
                element: <RequirePermission permission="marketing.campaign.view" />,
                children: [{ index: true, element: <WhatsappPage /> }],
              },
              {
                path: 'settings',
                element: <RequirePermission permission="marketing.campaign.view" />,
                children: [{ index: true, element: <MarketingSettingsPage /> }],
              },
            ],
          },

          {
            path: 'admin/users',
            element: <RequirePermission permission="admin.user.view" />,
            children: [{ index: true, element: <UsersPage /> }],
          },
          {
            path: 'admin/roles',
            element: <RequirePermission permission="admin.role.view" />,
            children: [{ index: true, element: <RolesPage /> }],
          },
          {
            path: 'admin/teams',
            element: <RequirePermission permission="admin.team.view" />,
            children: [{ index: true, element: <TeamsPage /> }],
          },
          {
            path: 'admin/organization',
            element: <RequirePermission permission="admin.organization.view" />,
            children: [{ index: true, element: <OrganizationPage /> }],
          },
          {
            path: 'settings/properties',
            element: <RequirePermission permission="admin.customfield.view" />,
            children: [{ index: true, element: <PropertiesPage /> }],
          },
          {
            path: 'crm/duplicates',
            element: <RequirePermission anyOf={['crm.contact.update', 'crm.company.update']} />,
            children: [{ index: true, element: <DuplicatesPage /> }],
          },
          {
            path: 'objects',
            element: <RequirePermission permission="crm.object.view" />,
            children: [
              { index: true, element: <ObjectsPage /> },
              { path: ':objectKey', element: <ObjectRecordsPage /> },
            ],
          },
          { path: 'admin/billing', element: <RequirePermission permission="admin.organization.view" />, children: [{ index: true, element: <BillingPage /> }] },
          { path: 'platform', element: <PlatformPage /> },
          {
            path: 'admin/audit',
            element: <RequirePermission permission="admin.audit.view" />,
            children: [{ index: true, element: <AuditPage /> }],
          },
          {
            path: 'admin/workflows',
            element: <RequirePermission permission="admin.workflow.view" />,
            children: [{ index: true, element: <WorkflowsPage /> }],
          },
          {
            path: 'admin/portal-users',
            element: <RequirePermission permission="portal.user.view" />,
            children: [{ index: true, element: <PortalUsersPage /> }],
          },

          {
            path: '*',
            element: (
              <EmptyState
                title="Page not found"
                description="That page does not exist, or has not been built yet."
                action={
                  <Button asChild variant="outline">
                    <a href="/dashboard">Back to dashboard</a>
                  </Button>
                }
              />
            ),
          },
        ],
      },
    ],
  },
]);
